'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useSession } from 'next-auth/react';
import { useParams, usePathname, useRouter } from 'next/navigation';
import { useTekstClient } from 'hooks/useTekstClient';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { resolveLangSlug } from 'utils/langSlug';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SourceDocument {
  wiki_item_id: number;
  content_id?: number;
  similarity: number | null;
  title?: string;
  url?: string;
}

interface FileAttachment {
  file: File;
  dataUrl: string;
}

interface Message {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  isError?: boolean;
  event_id?: number;
  sources?: SourceDocument[];
  context?: SourceDocument[];
  created_at?: string;
  attachments?: string[];
}

interface Conversation {
  id: number;
  title: string;
  message_count?: number;
  last_message_preview?: string;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SupportChatPage() {
  const { tekst } = useTekstClient();
  // tekst() returns a literal ' ' (not '' / null) for missing/loading translation keys,
  // which defeats a plain `tekst(key, null, null, true) || fallback` — ' ' is truthy in JS.
  const t = (key: string, fallback: string): string => {
    const value = tekst(key, null, null, true);
    return typeof value === 'string' && value.trim() ? value : fallback;
  };
  const { data: session, status } = useSession();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    if (status === 'unauthenticated') {
      router.replace(`/mijn-mkg/inloggen?intended=${encodeURIComponent(pathname)}`);
    }
  }, [status]);

  // Conversation state
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<number | null>(null);
  const [_conversationTitle, setConversationTitle] = useState<string>(tekst('pagina:support-chat:nieuwe-conversatie', null, null, true));
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Chat state
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [_streamingEventId, setStreamingEventId] = useState<number | null>(null);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);

  // Feedback state (keyed by event_id)
  type FeedbackState = { rating: 1 | -1 | null; comment: string; submitted: boolean; open: boolean };
  const [feedback, setFeedback] = useState<Record<number, FeedbackState>>({});

  // TopDesk ticket state — at most one ticket per conversation, not per message.
  // The confirmation (with ticket number) is shown as a Ferro message in the transcript itself,
  // not a separate banner — see submitTicketComplaint().
  type TopdeskTicketStatus = 'idle' | 'loading' | 'done' | 'attachment_failed' | 'error';
  const [conversationTicket, setConversationTicket] = useState<{
    status: TopdeskTicketStatus;
    ticketNumber: string | null;
  }>({ status: 'idle', ticketNumber: null });
  // While true, the main input box collects the user's complaint text for the ticket
  // instead of sending a normal chat message.
  const [ticketMode, setTicketMode] = useState(false);
  // Set once a ticket has been created from this conversation — the conversation is then read-only.
  const [conversationClosedAt, setConversationClosedAt] = useState<string | null>(null);

  // Attachment state
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);

  // SSE ref (for cleanup on unmount or new message)
  const sseRef = useRef<EventSource | null>(null);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamingBufferRef = useRef<string>('');
  const streamingTimerRef = useRef<number | null>(null);
  const scrollTimeoutRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const params = useParams();
  const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND || 'http://localhost:8443';
  const API_BASE = `${BACKEND_URL}/api/public/support`;       // session-cookie-based public endpoints
  const AUTH_API_BASE = `${BACKEND_URL}/api/mijn-mkg/support`; // Bearer-token authenticated endpoints

  // ---------------------------------------------------------------------------
  // Feedback helper
  // ---------------------------------------------------------------------------

  /** Switch the main input box into "describe your issue" mode for a TOPdesk ticket. */
  const enterTicketMode = useCallback(() => {
    if (conversationClosedAt || conversationTicket.status === 'loading' || conversationTicket.status === 'done') return;
    setTicketMode(true);
    setInputMessage('');
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [conversationClosedAt, conversationTicket.status]);

  const cancelTicketMode = useCallback(() => {
    setTicketMode(false);
    setInputMessage('');
  }, []);

  /** Submit the complaint typed in the main input box as the TOPdesk ticket description. */
  const submitTicketComplaint = useCallback(async () => {
    const complaint = inputMessage.trim();
    if (!complaint || !currentConversationId) return;

    setInputMessage('');
    setConversationTicket(prev => ({ ...prev, status: 'loading' }));

    try {
      const res = await fetch(`${AUTH_API_BASE}/topdesk/ticket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...((session as any)?.accessToken ? { 'Authorization': `Bearer ${(session as any).accessToken}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({ conversation_id: currentConversationId, description: complaint, language: getLanguage() }),
      });
      const data = await res.json();

      if (data.ticket_number) {
        setConversationTicket({
          status: data.attachment_failed ? 'attachment_failed' : 'done',
          ticketNumber: data.ticket_number,
        });
        setTicketMode(false); // done — hand the input back to normal chat
        // Re-fetch: the backend persisted both the complaint and the ticket-confirmation
        // message, plus closed_at — reload so the transcript and closed state match the server.
        await loadConversation(currentConversationId);
        loadConversations();
      } else {
        // Stay in ticket mode: the complaint is restored into the input, and Send should
        // retry the ticket, not silently fall through to a normal chat message.
        setConversationTicket(prev => ({ ...prev, status: 'error' }));
        setInputMessage(complaint);
        const reason = typeof data.message === 'string' ? data.message : null;
        const base = t('pagina:support-chat:topdesk:mislukt', 'Could not create the TOPdesk ticket.');
        setMessages(prev => [...prev, {
          id: Date.now(),
          role: 'assistant',
          content: reason ? `${base} (${reason})` : base,
          isError: true,
        }]);
      }
    } catch (e) {
      console.warn('Error creating TOPdesk ticket:', e);
      setConversationTicket(prev => ({ ...prev, status: 'error' }));
      setInputMessage(complaint);
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        content: t('pagina:support-chat:topdesk:mislukt-netwerk', 'Could not reach the server to create the TOPdesk ticket. Check your connection and try again.'),
        isError: true,
      }]);
    }
  }, [inputMessage, currentConversationId, session, AUTH_API_BASE]);

  const submitFeedback = useCallback(async (eventId: number, rating: 1 | -1, comment: string) => {
    setFeedback(prev => ({ ...prev, [eventId]: { rating, comment, submitted: true, open: false } }));
    try {
      await fetch(`${API_BASE}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_id: eventId, rating, comment }),
      });
    } catch (e) {
      // silent — UI already shows submitted state
      console.warn('Feedback submit error', e);
    }
  }, [API_BASE]);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /** Full lang slug for URLs (e.g. 'de-DE'); read at call time so async flows see the current URL */
  const getLangSlug = (): string =>
    resolveLangSlug({
      pathname: typeof window !== 'undefined' ? window.location.pathname : pathname,
      cookie: typeof document !== 'undefined' ? document.cookie : undefined,
      paramLang: params?.lang as string,
    });

  /** Base language code (e.g. 'de' from 'de-DE'), always consistent with the link slug */
  const getLanguage = (): string => getLangSlug().split('-')[0];

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const original = ev.target?.result as string;
        const img = new Image();
        img.onload = () => {
          const MAX = 1024;
          let w = img.width, h = img.height;
          if (w > MAX || h > MAX) {
            if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
            else { w = Math.round(w * MAX / h); h = MAX; }
          }
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
          setAttachments(prev => [...prev, { file, dataUrl }]);
        };
        img.onerror = () => {
          // Fallback: use original if canvas fails (e.g. SVG)
          setAttachments(prev => [...prev, { file, dataUrl: original }]);
        };
        img.src = original;
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  }, []);

  const removeAttachment = useCallback((index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items).filter(item => item.type.startsWith('image/'));
    if (items.length === 0) return;
    e.preventDefault();
    items.forEach(item => {
      const file = item.getAsFile();
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        const original = ev.target?.result as string;
        const img = new Image();
        img.onload = () => {
          const MAX = 1024;
          let w = img.width, h = img.height;
          if (w > MAX || h > MAX) {
            if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
            else { w = Math.round(w * MAX / h); h = MAX; }
          }
          const canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
          setAttachments(prev => [...prev, { file, dataUrl }]);
        };
        img.onerror = () => setAttachments(prev => [...prev, { file, dataUrl: original }]);
        img.src = original;
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  };

  // ---------------------------------------------------------------------------
  // Wiki title helpers (shared by streaming completion & conversation load)
  // ---------------------------------------------------------------------------

  const fetchWikiTitles = async (
    wikiItemIds: number[],
    language?: string,
  ): Promise<Record<number, { title: string; id: number }>> => {
    try {
      const res = await fetch(`${API_BASE}/wiki-titles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ wiki_item_ids: wikiItemIds, language: language ?? getLanguage() }),
      });
      const data = await res.json();
      if (data.success && data.titles) {
        const map: Record<number, { title: string; id: number }> = {};
        Object.keys(data.titles).forEach((id) => {
          map[Number(id)] = data.titles[id];
        });
        return map;
      }
      return {};
    } catch (error) {
      console.warn('Error fetching wiki titles:', error);
      return {};
    }
  };

  /** Resolve raw context items into SourceDocument[] with human-readable titles */
  const resolveSourceTitles = async (context: any[]): Promise<SourceDocument[]> => {
    if (!context || !Array.isArray(context) || context.length === 0) return [];

    const wikiItemIds = context.map((s: any) =>
      typeof s === 'object' ? s.wiki_item_id : s,
    );
    const lang = getLangSlug();
    const titleMap = await fetchWikiTitles(wikiItemIds, lang.split('-')[0]);

    return context
      .map((source: any) => {
        const itemId = typeof source === 'object' ? source.wiki_item_id : source;
        const similarity = typeof source === 'object' ? source.similarity : null;
        const info = titleMap[itemId];
        if (!info) return null;
        return {
          wiki_item_id: itemId,
          similarity,
          title: info.title,
          url: `/${lang}/mijn-mkg/support/kenniscentrum/id/${info.id}`,
        } as SourceDocument;
      })
      .filter(Boolean) as SourceDocument[];
  };

  // ---------------------------------------------------------------------------
  // Streaming handler (SSE)
  // ---------------------------------------------------------------------------

  const handleStreamingPayload = useCallback((payload: any) => {
    const { status, chunk, full_answer, context, error_message, event_id } = payload;

    if (status === 'streaming' && chunk) {
      // Buffer chunks and flush via rAF for smooth 60 fps rendering
      streamingBufferRef.current += chunk;

      if (streamingTimerRef.current === null) {
        streamingTimerRef.current = requestAnimationFrame(() => {
          const buffered = streamingBufferRef.current;
          streamingBufferRef.current = '';
          streamingTimerRef.current = null;

          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant' && last.isStreaming) {
              return [...prev.slice(0, -1), { ...last, content: last.content + buffered }];
            }
            return prev;
          });
        }) as unknown as number;
      }
    } else if (status === 'completed') {
      finalizeStreamedMessage(event_id, full_answer, context);
    } else if (status === 'failed') {
      const is413 = error_message?.includes('413');
      const friendlyContent = is413
        ? tekst('pagina:support-chat:fout:te-lang', null, null, true)
        : error_message || tekst('pagina:support-chat:fout:verzoek-mislukt', null, null, true);
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant') {
          return [
            ...prev.slice(0, -1),
            {
              ...last,
              content: friendlyContent,
              isStreaming: false,
              isError: true,
            },
          ];
        }
        return prev;
      });
      setIsLoading(false);
      setStreamingEventId(null);
    }
  }, []);

  /** Finalize a streamed message: set final content, resolve source titles, refresh sidebar */
  const finalizeStreamedMessage = async (
    eventId: number,
    fullAnswer: string | null,
    context: any[] | null,
  ) => {
    const sources = await resolveSourceTitles(context ?? []);

    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last?.role === 'assistant') {
        return [
          ...prev.slice(0, -1),
          {
            ...last,
            content: fullAnswer || last.content,
            isStreaming: false,
            event_id: eventId,
            sources,
          },
        ];
      }
      return prev;
    });

    setIsLoading(false);
    setStreamingEventId(null);

    // Refresh sidebar so the new / updated conversation appears
    setTimeout(() => loadConversations(), 500);
  };

  // ---------------------------------------------------------------------------
  // Auto-scroll on message change
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (scrollTimeoutRef.current) cancelAnimationFrame(scrollTimeoutRef.current);

    const isStreaming = messages.some((m) => m.isStreaming && m.content);

    scrollTimeoutRef.current = requestAnimationFrame(() => {
      if (isStreaming) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
      } else {
        scrollToBottom();
      }
    }) as unknown as number;

    return () => {
      if (scrollTimeoutRef.current) cancelAnimationFrame(scrollTimeoutRef.current);
    };
  }, [messages]);

  // Close sidebar by default on mobile
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  }, []);

  // Clean up any active SSE connection on unmount
  useEffect(() => {
    return () => {
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
    };
  }, []);



  // Load conversations once the session (and its Bearer token) is available
  useEffect(() => {
    if ((session as any)?.accessToken) {
      loadConversations();
    }
  }, [(session as any)?.accessToken]);

  // ---------------------------------------------------------------------------
  // Conversation CRUD
  // ---------------------------------------------------------------------------

  const loadConversations = async () => {
    if (!(session as any)?.accessToken) return;
    try {
      const res = await fetch(`${AUTH_API_BASE}/conversations`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${(session as any).accessToken}`,
        },
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.success) setConversations(data.conversations ?? []);
    } catch (e) {
      console.warn('Error loading conversations:', e);
    }
  };

  const loadConversation = async (conversationId: number) => {
    try {
      const res = await fetch(`${AUTH_API_BASE}/conversations/${conversationId}`, {
        credentials: 'include',
        headers: {
          ...((session as any)?.accessToken ? { 'Authorization': `Bearer ${(session as any).accessToken}` } : {}),
        },
      });
      const data = await res.json();
      if (!data.success) return;

      const sorted: Message[] = (data.messages || []).sort(
        (a: Message, b: Message) => a.id - b.id,
      );

      // Collect all wiki_item_ids across every assistant message's context
      const allWikiIds: number[] = [];
      sorted.forEach((m) => {
        const ctx = (m as any).context as SourceDocument[] | undefined;
        ctx?.forEach((d) => {
          if (!allWikiIds.includes(d.wiki_item_id)) allWikiIds.push(d.wiki_item_id);
        });
      });

      // Batch-resolve titles
      if (allWikiIds.length > 0) {
        const lang = getLangSlug();
        const titleMap = await fetchWikiTitles(allWikiIds, lang.split('-')[0]);
        sorted.forEach((m) => {
          const ctx = (m as any).context;
          if (ctx && Array.isArray(ctx)) {
            m.sources = ctx.map((d: SourceDocument) => ({
              ...d,
              title: titleMap[d.wiki_item_id]?.title || `Document ${d.wiki_item_id}`,
              url: `/${lang}/mijn-mkg/support/kenniscentrum/id/${titleMap[d.wiki_item_id]?.id ?? d.wiki_item_id}`,
            }));
          }
        });
      }

      setMessages(sorted);
      setCurrentConversationId(conversationId);
      setConversationTitle(
        data.conversation?.title ??
          conversations.find((c) => c.id === conversationId)?.title ??
          'Conversation',
      );
      setConversationTicket(
        data.conversation?.topdesk_ticket_number
          ? { status: 'done', ticketNumber: data.conversation.topdesk_ticket_number }
          : { status: 'idle', ticketNumber: null },
      );
      setConversationClosedAt(data.conversation?.closed_at ?? null);
    } catch (e) {
      console.warn('Error loading conversation:', e);
    }
  };

  const startNewConversation = () => {
    setCurrentConversationId(null);
    setMessages([]);
    setConversationTitle(tekst('pagina:support-chat:nieuwe-conversatie', null, null, true));
    setConversationTicket({ status: 'idle', ticketNumber: null });
    setConversationClosedAt(null);
    setTicketMode(false);
    inputRef.current?.focus();
  };

  // ---------------------------------------------------------------------------
  // Send message
  // ---------------------------------------------------------------------------

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    setSubscriptionError(null);

    const userMessage = inputMessage;
    const currentAttachments = attachments.map(a => a.dataUrl);
    setInputMessage('');
    setAttachments([]);

    // Append user message (don't clear previous messages — this is a conversation)
    const tempUserMsg: Message = { id: Date.now(), role: 'user', content: userMessage, attachments: currentAttachments.length > 0 ? currentAttachments : undefined };
    setMessages((prev) => [...prev, tempUserMsg]);

    // Thinking placeholder
    const thinkingMsg: Message = {
      id: Date.now() + 1,
      role: 'assistant',
      content: '',
      isStreaming: true,
    };
    setMessages((prev) => [...prev, thinkingMsg]);
    setIsLoading(true);

    // Optimistic title for new conversations
    if (!currentConversationId) {
      const title =
        userMessage.length > 50 ? userMessage.substring(0, 50) + '...' : userMessage;
      setConversationTitle(title);
    }

    try {
      const response = await fetch(`${AUTH_API_BASE}/conversations/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...((session as any)?.accessToken ? { 'Authorization': `Bearer ${(session as any).accessToken}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          message: userMessage,
          conversation_id: currentConversationId,
          language: getLanguage(),
          ...(currentAttachments.length > 0 ? { attachments: currentAttachments } : {}),
        }),
      });

      const data = await response.json();

      if (response.status === 429) {
        setIsLoading(false);
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.isStreaming) {
            return [
              ...prev.slice(0, -1),
              {
                ...last,
                content: tekst('pagina:support-chat:fout:te-veel-verzoeken', null, null, true),
                isStreaming: false,
                isError: true,
              },
            ];
          }
          return prev;
        });
        return;
      }

      if (data.code === 'NO_SUBSCRIPTION' || data.code === 'UNAUTHENTICATED') {
        setSubscriptionError(data.code);
        setIsLoading(false);
        setMessages((prev) => prev.filter((m) => !m.isStreaming));
        return;
      }

      if (data.success) {
        const eventId: number = data.event_id;
        const channelToken: string | undefined = data.channel_token;
        const newConvId: number = data.conversation_id;

        setStreamingEventId(eventId);

        // Track the conversation id returned by the backend
        if (!currentConversationId && newConvId) {
          setCurrentConversationId(newConvId);
        }

        if (channelToken) {
          // ---- SSE streaming path ----
          // Close any previous SSE connection
          if (sseRef.current) {
            sseRef.current.close();
            sseRef.current = null;
          }

          const sseUrl = `${API_BASE}/stream/${eventId}?channel_token=${encodeURIComponent(channelToken)}`;
          const eventSource = new EventSource(sseUrl, { withCredentials: true });
          sseRef.current = eventSource;

          eventSource.addEventListener('message', (e) => {
            try {
              const payload = JSON.parse(e.data);
              handleStreamingPayload(payload);
            } catch (err) {
              console.warn('SSE parse error:', err);
            }
          });

          eventSource.addEventListener('done', () => {
            eventSource.close();
            sseRef.current = null;
          });

          eventSource.addEventListener('error', () => {
            console.warn('⚠️ SSE connection error, falling back to polling');
            eventSource.close();
            sseRef.current = null;
            pollForResponse(eventId);
          });

          // Safety timeout: 60 s with no tokens → polling
          const fallbackTimeout = setTimeout(() => {
            setMessages((prev) => {
              const lastMsg = prev[prev.length - 1];
              if (lastMsg?.isStreaming && !lastMsg.content) {
                console.warn('⚠️ No streaming data via SSE, falling back to polling');
                eventSource.close();
                sseRef.current = null;
                pollForResponse(eventId);
              }
              return prev;
            });
          }, 60000);

          // Clean up timeout when streaming completes
          const cleanupInterval = setInterval(() => {
            setStreamingEventId((cur) => {
              if (cur === null) {
                clearTimeout(fallbackTimeout);
                clearInterval(cleanupInterval);
                if (sseRef.current === eventSource) {
                  eventSource.close();
                  sseRef.current = null;
                }
              }
              return cur;
            });
          }, 1000);
        } else {
          // ---- Polling fallback ----
          console.warn('⚠️ No channel token, polling…');
          pollForResponse(eventId);
        }
      } else {
        throw new Error(data.message || 'Request failed');
      }
    } catch (error) {
      console.warn('Error sending message:', error);
      setIsLoading(false);
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.isStreaming) {
          return [
            ...prev.slice(0, -1),
            {
              ...last,
              content: tekst('pagina:support-chat:fout:algemeen', null, null, true),
              isStreaming: false,
              isError: true,
            },
          ];
        }
        return prev;
      });
    }
  };

  // ---------------------------------------------------------------------------
  // Polling fallback
  // ---------------------------------------------------------------------------

  const pollForResponse = async (eventId: number) => {
    const maxAttempts = 30;
    let attempts = 0;

    const poll = async () => {
      try {
        const res = await fetch(`${AUTH_API_BASE}/event/${eventId}`, {
          credentials: 'include',
          headers: {
            ...((session as any)?.accessToken ? { 'Authorization': `Bearer ${(session as any).accessToken}` } : {}),
          },
        });
        const data = await res.json();

        if (data.success) {
          if (data.status === 'completed' && data.answer) {
            const sources = data.context
              ? await resolveSourceTitles(data.context)
              : [];

            setMessages((prev) => {
              const last = prev[prev.length - 1];
              if (last?.role === 'assistant') {
                return [
                  ...prev.slice(0, -1),
                  {
                    ...last,
                    content: data.answer,
                    isStreaming: false,
                    event_id: data.event_id,
                    sources,
                  },
                ];
              }
              return prev;
            });
            setIsLoading(false);
            setStreamingEventId(null);
            setTimeout(() => loadConversations(), 500);
            return;
          }

          if (data.status === 'failed' || data.status === 'error') {
            const is413 = data.error_message?.includes('413');
            const friendlyContent = is413
              ? tekst('pagina:support-chat:fout:te-lang', null, null, true)
              : data.error_message || tekst('pagina:support-chat:fout:verzoek-mislukt', null, null, true);
            setMessages((prev) => {
              const last = prev[prev.length - 1];
              if (last?.role === 'assistant') {
                return [
                  ...prev.slice(0, -1),
                  {
                    ...last,
                    content: friendlyContent,
                    isStreaming: false,
                  },
                ];
              }
              return prev;
            });
            setIsLoading(false);
            setStreamingEventId(null);
            return;
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000);
        } else {
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant') {
              return [
                ...prev.slice(0, -1),
                { ...last, content: tekst('pagina:support-chat:fout:timeout', null, null, true), isStreaming: false, isError: true },
              ];
            }
            return prev;
          });
          setIsLoading(false);
          setStreamingEventId(null);
        }
      } catch {
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000);
        } else {
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant') {
              return [
                ...prev.slice(0, -1),
                { ...last, content: tekst('pagina:support-chat:fout:ophalen', null, null, true), isStreaming: false, isError: true },
              ];
            }
            return prev;
          });
          setIsLoading(false);
          setStreamingEventId(null);
        }
      }
    };

    poll();
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const userMessageCount = messages.filter((m) => m.role === 'user').length;

  const ferroAvatarUrl = "/images/ferro-avatar.png";

  return (
    <>
      {/* ---- Global CSS animations ---- */}
      <style jsx global>{`
        .ferro-page {
          background-color: #ecf0f9;
        }

        /* ── Message entrance — fires once on mount, not on content updates ── */
        .message-bubble {
          animation: msgEnter 0.2s ease-out both;
          will-change: opacity, transform;
          /* Text stays selectable/copyable, but no blinking text-input caret on click */
          caret-color: transparent;
        }
        @keyframes msgEnter {
          from { opacity: 0; transform: translateY(5px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* ── Streaming bubble — CSS transition, no looping keyframe ── */
        .streaming-bubble-active {
          border-color: rgba(35, 193, 244, 0.38) !important;
          box-shadow: 0 2px 14px rgba(35, 193, 244, 0.09), 0 1px 3px rgba(0,0,0,0.04) !important;
          transition: border-color 0.35s ease, box-shadow 0.35s ease;
        }

        /* ── Inline caret — dedicated element, never jumps between DOM nodes ── */
        .ferro-caret {
          display: inline-block;
          width: 2px;
          height: 0.88em;
          background: #23c1f4;
          border-radius: 1px;
          margin-left: 3px;
          vertical-align: text-bottom;
          will-change: opacity;
          animation: caretBlink 0.95s ease-in-out infinite;
        }
        @keyframes caretBlink {
          0%, 42%  { opacity: 1; }
          58%, 100% { opacity: 0; }
        }

        /* ── Thinking dots — natural staggered bounce ── */
        .thinking-dot {
          animation: thinkBounce 1.3s cubic-bezier(0.45, 0, 0.55, 1) infinite;
          will-change: opacity, transform;
        }
        .thinking-dot:nth-child(2) { animation-delay: 0.18s; }
        .thinking-dot:nth-child(3) { animation-delay: 0.36s; }
        @keyframes thinkBounce {
          0%, 55%, 100% { opacity: 0.35; transform: translateY(0); }
          27%           { opacity: 1;    transform: translateY(-5px); }
        }

        /* ── Action buttons ── */
        .ferro-action-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 5px;
          border-radius: 6px;
          cursor: pointer;
          color: #82a3bd;
          background: transparent;
          border: none;
          transition: color 0.15s ease, background-color 0.15s ease;
        }
        .ferro-action-btn:hover:not(:disabled) {
          color: #005188;
          background-color: rgba(0, 81, 136, 0.07);
        }
        .ferro-action-btn:disabled { cursor: not-allowed; }
        .ferro-action-btn.btn-done { color: #059669; }
        .ferro-action-btn.btn-done:hover { color: #059669; background-color: rgba(5,150,105,0.07); }
        .ferro-action-btn.btn-error { color: #dc2626; }
        .ferro-action-btn.btn-error:hover { color: #dc2626; background-color: rgba(220,38,38,0.07); }

        /* ── Respect user's motion preference ── */
        @media (prefers-reduced-motion: reduce) {
          .message-bubble  { animation: none; }
          .ferro-caret     { animation: none; opacity: 1; }
          .thinking-dot    { animation: none; opacity: 0.6; }
        }

        .ferro-sidebar {
          width: 224px;
          flex-shrink: 0;
          transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
          overflow: hidden;
          background: white;
          border-right: 1px solid rgba(201,214,241,0.6);
        }
        .ferro-sidebar.closed {
          width: 0;
          opacity: 0;
          border-right-width: 0;
        }
        .ferro-sidebar-inner {
          width: 224px;
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .ferro-chat-wrapper {
          position: relative;
        }

        .ferro-messages-area {
          background-color: #ECF0F9;
        }

        .ferro-mobile-backdrop {
          display: none;
          position: absolute;
          inset: 0;
          background: rgba(0,0,0,0.35);
          z-index: 40;
          cursor: pointer;
        }

        @media (max-width: 767px) {
          .ferro-sidebar {
            position: absolute !important;
            z-index: 50;
            height: 100%;
            top: 0;
            left: 0;
            box-shadow: 4px 0 24px rgba(0,0,0,0.14);
          }
          .ferro-mobile-backdrop.sidebar-visible {
            display: block;
          }
          .ferro-page-header {
            padding-left: 12px !important;
            padding-right: 12px !important;
          }
          .ferro-input-area {
            padding-left: 12px !important;
            padding-right: 12px !important;
          }
          .ferro-messages-area {
            padding-left: 12px !important;
            padding-right: 12px !important;
            background-color: #ECF0F9 !important;
          }
          .ferro-action-btn {
            padding: 7px !important;
          }
        }
      `}</style>

      {/* Full-page ferro background — override global <main> margins for this page */}
      <div className="ferro-page flex flex-col" style={{ height: 'calc(100dvh - 200px)', overflow: 'hidden', marginTop: '-64px', marginBottom: '-64px' }} suppressHydrationWarning>

        {/* ── Top header bar ──────────────────────────────────────────── */}
        <div className="ferro-page-header bg-white flex items-center justify-between px-6 py-0 flex-shrink-0" style={{ borderBottom: '1px solid rgba(201,214,241,0.6)', height: '72px' }}>
          <div className="flex items-center gap-3">
            {/* Sidebar toggle */}
            <button
              onClick={() => setSidebarOpen((v) => !v)}
              className="p-2 rounded-lg hover:bg-[#f1f5f9] transition text-[#475569]"
              title={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            {/* Ferro logo + title */}
            <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
              <img src={ferroAvatarUrl} alt="Ferro" className="w-full h-full object-cover" />
            </div>
            <span className="text-[28px] font-bold tracking-tight" style={{ color: '#005188' }}>Ferro</span>
          </div>
        </div>

        {/* ── Subscription error banner ────────────────────────────────── */}
        {subscriptionError && (
          <div className="w-full bg-amber-50 border-b-2 border-amber-400 px-6 py-3 flex items-start gap-4 flex-shrink-0">
            <div className="text-amber-500 mt-0.5 flex-shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="font-semibold text-amber-800 text-sm">
                {subscriptionError === 'NO_SUBSCRIPTION'
                  ? tekst('pagina:support-chat:geen-abonnement:titel', null, null, true)
                  : tekst('pagina:support-chat:niet-ingelogd:titel', null, null, true)}
              </p>
              <p className="text-amber-700 text-xs mt-0.5">
                {subscriptionError === 'NO_SUBSCRIPTION'
                  ? tekst('pagina:support-chat:geen-abonnement:tekst', null, null, true)
                  : tekst('pagina:support-chat:niet-ingelogd:tekst', null, null, true)}
              </p>
            </div>
          </div>
        )}

        {/* ── Body: sidebar + chat ─────────────────────────────────────── */}
        <div className="ferro-chat-wrapper flex flex-1 min-h-0">

          {/* Mobile backdrop */}
          <div
            className={`ferro-mobile-backdrop${sidebarOpen ? ' sidebar-visible' : ''}`}
            onClick={() => setSidebarOpen(false)}
          />

          {/* ── Sidebar ─────────────────────────────────────────────────── */}
          <div className={`ferro-sidebar${sidebarOpen ? '' : ' closed'}`}>
            <div className="ferro-sidebar-inner">
              {/* New conversation */}
              <div className="p-4" style={{ borderBottom: '1px solid rgba(201,214,241,0.6)' }}>
                <button
                  onClick={startNewConversation}
                  className="w-full font-semibold py-3 px-4 rounded-full text-white text-sm transition-all flex items-center justify-center"
                  style={{ backgroundColor: '#23c1f4' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1ba8d6')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#23c1f4')}
                >
                  {tekst('pagina:support-chat:sidebar:nieuwe-conversatie')}
                </button>
              </div>

              {/* Conversation list */}
              <div className="flex-1 overflow-y-auto p-3">
                {conversations.length === 0 ? (
                  <p className="text-[#475569] text-center py-8 text-sm">
                    {tekst('pagina:support-chat:sidebar:geen-conversaties')}
                  </p>
                ) : (
                  conversations.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => loadConversation(conv.id)}
                      className="w-full text-left px-3 py-3 rounded-xl mb-1.5 transition-all"
                      style={{
                        backgroundColor: currentConversationId === conv.id ? '#005188' : 'transparent',
                        color: currentConversationId === conv.id ? '#ffffff' : '#334155',
                      }}
                      onMouseEnter={(e) => {
                        if (currentConversationId !== conv.id)
                          e.currentTarget.style.backgroundColor = '#f1f5f9';
                      }}
                      onMouseLeave={(e) => {
                        if (currentConversationId !== conv.id)
                          e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <div className="font-semibold truncate text-sm">{conv.title}</div>
                      <div className="text-xs mt-0.5 flex items-center gap-1.5"
                        style={{ color: currentConversationId === conv.id ? 'rgba(255,255,255,0.7)' : '#94a3b8' }}>
                        <span>{new Date(conv.updated_at).toLocaleDateString('nl-NL', { month: 'short', day: 'numeric' })}</span>
                        {conv.message_count != null && <span>· {conv.message_count} msgs</span>}
                      </div>
                      {conv.last_message_preview && (
                        <div className="text-xs mt-0.5 truncate"
                          style={{ color: currentConversationId === conv.id ? 'rgba(255,255,255,0.55)' : '#cbd5e1' }}>
                          {conv.last_message_preview}
                        </div>
                      )}
                    </button>
                  ))
                )}
              </div>

              {/* Sidebar footer */}
              <div className="p-4 text-xs text-[#94a3b8]" style={{ borderTop: '1px solid rgba(201,214,241,0.6)' }}>
                <div className="font-semibold mb-0.5 text-[#475569]">{tekst('pagina:support-chat:sidebar:footer:label')}</div>
                <div>{tekst('pagina:support-chat:sidebar:footer:tekst')}</div>
              </div>
            </div>
          </div>

          {/* ── Main chat column ─────────────────────────────────────────── */}
          <div className="flex-1 flex flex-col min-w-0 min-h-0">

            {/* Messages scroll area */}
            <div className="ferro-messages-area flex-1 overflow-y-auto px-6 py-6" style={{ scrollbarGutter: 'stable' }}>
            <div className="max-w-4xl mx-auto w-full">

              {/* Empty state */}
              {messages.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-center">
                  <div className="w-16 h-16 rounded-full overflow-hidden mb-4 shadow-md">
                    <img src={ferroAvatarUrl} alt="Ferro" className="w-full h-full object-cover" />
                  </div>
                  <h2 className="text-xl font-bold mb-1" style={{ color: '#005188' }}>
                    <span className="font-bold">Ferro</span>
                    <span className="font-light" style={{ color: '#475569' }}>, {tekst('pagina:support-chat:welkom:subtitel', null, null, true)}</span>
                  </h2>
                  <p className="text-[#475569] text-sm max-w-sm mt-2">
                    {tekst('pagina:support-chat:welkom:omschrijving')}
                  </p>
                  <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3 max-w-xl w-full">
                    <div className="p-4 bg-white rounded-2xl text-left shadow-sm" style={{ border: '1px solid rgba(201,214,241,0.6)' }}>
                      <div className="font-semibold text-[#005188] text-sm mb-2">
                        {tekst('pagina:support-chat:welkom:voorbeelden:titel')}
                      </div>
                      <ul className="text-sm space-y-1.5 list-disc list-inside text-[#475569]">
                        <li>{tekst('pagina:support-chat:welkom:voorbeelden:vraag1')}</li>
                        <li>{tekst('pagina:support-chat:welkom:voorbeelden:vraag2')}</li>
                        <li>{tekst('pagina:support-chat:welkom:voorbeelden:vraag3')}</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-white rounded-2xl text-left shadow-sm" style={{ border: '1px solid rgba(201,214,241,0.6)' }}>
                      <div className="font-semibold text-[#005188] text-sm mb-2">
                        {tekst('pagina:support-chat:welkom:documentatie:titel')}
                      </div>
                      <p className="text-sm text-[#475569]">
                        {tekst('pagina:support-chat:welkom:documentatie:tekst')}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Message list */}
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`mb-4 flex items-end gap-3 message-bubble ${
                    message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                  }`}
                >
                  {/* Ferro avatar (assistant only) */}
                  {message.role === 'assistant' && (
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mb-1 shadow-sm">
                      <img src={ferroAvatarUrl} alt="Ferro" className="w-full h-full object-cover" />
                    </div>
                  )}

                  <div className={`flex flex-col ${message.role === 'user' ? 'items-end' : 'items-start'} max-w-[75%]`}>
                    {/* Bubble */}
                    <div
                      className={`rounded-2xl px-4 py-3 text-base leading-relaxed shadow-sm ${
                        message.isError
                          ? 'bg-red-50 text-red-700'
                          : message.role === 'user'
                          ? 'text-[#475569]'
                          : 'bg-white text-[#334155]'
                      }${message.isStreaming && message.content && message.role === 'assistant' ? ' streaming-bubble-active' : ''}`}
                      style={
                        message.isError
                          ? { border: '1px solid #fca5a5' }
                          : message.role === 'user'
                          ? { backgroundColor: '#dce6fa' }
                          : { border: '1px solid rgba(201,214,241,0.5)' }
                      }
                    >
                      {message.content ? (
                        message.isError ? (
                          <div className="flex items-start gap-2">
                            <svg className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                            </svg>
                            <span>{message.content}</span>
                          </div>
                        ) : message.role === 'assistant' ? (
                          <div>
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              rehypePlugins={[rehypeSanitize]}
                              components={{
                                strong: ({ children }) => <strong className="font-bold" style={{ color: '#005188' }}>{children}</strong>,
                                em: ({ children }) => <em className="italic" style={{ color: '#005188' }}>{children}</em>,
                                code: ({ children, className }) => {
                                  const isInline = !className;
                                  return isInline ? (
                                    <code className="bg-[#f1f5f9] px-1 py-0.5 rounded text-xs font-mono" style={{ color: '#005188' }}>{children}</code>
                                  ) : (
                                    <code className="block bg-[#f1f5f9] p-3 rounded-xl text-xs font-mono whitespace-pre-wrap" style={{ color: '#005188' }}>{children}</code>
                                  );
                                },
                                ul: ({ children }) => <ul className="list-disc list-inside space-y-1 my-2 text-[#334155]">{children}</ul>,
                                ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 my-2 text-[#334155]">{children}</ol>,
                                li: ({ children }) => <li className="text-base">{children}</li>,
                                h1: ({ children }) => <h1 className="text-lg font-bold my-3" style={{ color: '#005188' }}>{children}</h1>,
                                h2: ({ children }) => <h2 className="text-base font-bold my-2" style={{ color: '#005188' }}>{children}</h2>,
                                h3: ({ children }) => <h3 className="text-sm font-bold my-2" style={{ color: '#005188' }}>{children}</h3>,
                                blockquote: ({ children }) => <blockquote className="border-l-4 border-[#23c1f4] pl-3 my-2 text-[#475569] italic">{children}</blockquote>,
                                p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                                a: ({ href, children }) => (
                                  <a href={href} className="underline hover:opacity-75 transition-opacity" style={{ color: '#23c1f4' }} target="_blank" rel="noopener noreferrer">
                                    {children}
                                  </a>
                                ),
                              }}
                            >
                              {message.content}
                            </ReactMarkdown>
                            {message.isStreaming && (
                              <span className="ferro-caret" aria-hidden="true" />
                            )}
                          </div>
                        ) : (
                          <div>
                            {message.attachments && message.attachments.length > 0 && (
                              <div className="flex flex-wrap gap-2 mb-2">
                                {message.attachments.map((src, i) => (
                                  <img
                                    key={i}
                                    src={src}
                                    alt={`bijlage ${i + 1}`}
                                    className="max-h-48 max-w-full rounded-xl object-cover shadow-sm"
                                    style={{ border: '1px solid rgba(201,214,241,0.5)' }}
                                  />
                                ))}
                              </div>
                            )}
                            {message.content && <div className="whitespace-pre-wrap">{message.content}</div>}
                          </div>
                        )
                      ) : (
                        message.isStreaming && (
                          /* Thinking dots */
                          <div className="flex items-center gap-1.5 px-1 py-1">
                            <div className="w-2 h-2 rounded-full thinking-dot" style={{ backgroundColor: '#23c1f4' }} />
                            <div className="w-2 h-2 rounded-full thinking-dot" style={{ backgroundColor: '#23c1f4' }} />
                            <div className="w-2 h-2 rounded-full thinking-dot" style={{ backgroundColor: '#23c1f4' }} />
                          </div>
                        )
                      )}
                    </div>

                    {/* Source documents (assistant only) */}
                    {message.role === 'assistant' && !message.isStreaming && message.sources && message.sources.length > 0 && (
                      <div className="mt-2 w-full space-y-1.5">
                        <div className="text-xs font-semibold text-[#475569] px-1">
                          {tekst('pagina:support-chat:bronnen:titel')} ({message.sources.length})
                        </div>
                        {message.sources.map((source, idx) => (
                          <div key={idx} className="bg-white rounded-xl px-3 py-2.5 text-sm shadow-sm" style={{ border: '1px solid rgba(201,214,241,0.6)' }}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                {source.url ? (
                                  <a href={source.url} target="_blank" rel="noopener noreferrer"
                                    className="font-semibold text-sm underline hover:opacity-75 transition-opacity truncate block" style={{ color: '#23c1f4' }}>
                                    {source.title}
                                  </a>
                                ) : (
                                  <span className="font-semibold text-sm text-[#334155] truncate block">
                                    {source.title || `Document ${source.wiki_item_id}`}
                                  </span>
                                )}
                              </div>
                              {source.similarity != null && (
                                <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-white flex-shrink-0" style={{ backgroundColor: '#005188' }}>
                                  {Math.ceil(source.similarity * 100)}%
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Feedback row (assistant only) */}
                    {message.role === 'assistant' && !message.isStreaming && message.event_id && (() => {
                      const fb = feedback[message.event_id];
                      const eventId = message.event_id;

                      const ThumbUp = ({ filled }: { filled?: boolean }) => (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3zM7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3" />
                        </svg>
                      );
                      const ThumbDown = ({ filled }: { filled?: boolean }) => (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3zm7-13h2.67A2.31 2.31 0 0122 4v7a2.31 2.31 0 01-2.33 2H17" />
                        </svg>
                      );

                      if (fb?.submitted) {
                        const ts = conversationTicket.status;
                        const offerTicket = fb.rating === -1 && (ts === 'idle' || ts === 'error') && !ticketMode;
                        return (
                          <div className="mt-1.5 space-y-1.5 px-1">
                            <div className="flex items-center gap-1.5 text-xs text-[#94a3b8]">
                              <span className={fb.rating === 1 ? 'text-emerald-500' : 'text-red-400'}>
                                {fb.rating === 1 ? <ThumbUp filled /> : <ThumbDown filled />}
                              </span>
                              <span>{tekst('pagina:support-chat:feedback:bedankt')}</span>
                            </div>
                            {offerTicket && (
                              <button
                                onClick={enterTicketMode}
                                className="text-xs font-semibold underline hover:opacity-75 transition-opacity"
                                style={{ color: '#23c1f4' }}
                              >
                                {t('pagina:support-chat:topdesk:suggestie', 'Not the answer you needed? Create a helpdesk ticket')}
                              </button>
                            )}
                          </div>
                        );
                      }

                      if (fb?.open) {
                        const canSubmit = fb.rating === 1 || fb.rating === -1;
                        return (
                          <div className="mt-2 bg-white rounded-2xl p-3 space-y-2 shadow-sm w-full max-w-sm" style={{ border: '1px solid rgba(201,214,241,0.6)' }}>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setFeedback(prev => ({ ...prev, [eventId]: { ...prev[eventId], rating: 1 } }))}
                                className={`p-1.5 rounded-lg transition-colors ${fb.rating === 1 ? 'bg-emerald-100 text-emerald-600' : 'text-[#94a3b8] hover:text-emerald-500 hover:bg-emerald-50'}`}
                              ><ThumbUp filled={fb.rating === 1} /></button>
                              <button
                                onClick={() => setFeedback(prev => ({ ...prev, [eventId]: { ...prev[eventId], rating: -1 } }))}
                                className={`p-1.5 rounded-lg transition-colors ${fb.rating === -1 ? 'bg-red-100 text-red-500' : 'text-[#94a3b8] hover:text-red-400 hover:bg-red-50'}`}
                              ><ThumbDown filled={fb.rating === -1} /></button>
                              <button
                                onClick={() => setFeedback(prev => { const n = { ...prev }; delete n[eventId]; return n; })}
                                className="ml-auto text-xs text-[#94a3b8] hover:text-[#475569] transition-colors"
                              >&#x2715;</button>
                            </div>
                            <textarea
                              rows={2}
                              placeholder={tekst('pagina:support-chat:feedback:toelichting', null, null, true)}
                              value={fb.comment}
                              onChange={e => setFeedback(prev => ({ ...prev, [eventId]: { ...prev[eventId], comment: e.target.value } }))}
                              className="w-full text-sm rounded-xl px-3 py-2 resize-none focus:outline-none transition"
                              style={{ border: '1px solid rgba(201,214,241,0.8)' }}
                            />
                            <button
                              disabled={!canSubmit}
                              onClick={() => canSubmit && submitFeedback(eventId, fb.rating as 1 | -1, fb.comment)}
                              className={`text-sm font-semibold rounded-full px-4 py-1.5 transition-colors ${canSubmit ? 'text-white' : 'bg-[#f1f5f9] text-[#94a3b8] cursor-not-allowed'}`}
                              style={canSubmit ? { backgroundColor: '#23c1f4' } : {}}
                            >
                              {tekst('pagina:support-chat:invoer:verstuur')}
                            </button>
                          </div>
                        );
                      }

                      return (
                        <div className="mt-2 flex items-center gap-0.5 px-0.5">
                          {/* Copy */}
                          <button
                            onClick={() => navigator.clipboard?.writeText(message.content)}
                            className="ferro-action-btn"
                            title={tekst('pagina:support-chat:feedback:kopieren', null, null, true)}
                            aria-label={tekst('pagina:support-chat:feedback:kopieren', null, null, true)}
                          >
                            <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                          {/* Thumbs up */}
                          <button
                            onClick={() => setFeedback(prev => ({ ...prev, [eventId]: { rating: 1, comment: '', submitted: false, open: true } }))}
                            className="ferro-action-btn"
                            title={tekst('pagina:support-chat:feedback:goed', null, null, true)}
                            aria-label={tekst('pagina:support-chat:feedback:goed', null, null, true)}
                          >
                            <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3zM7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3" />
                            </svg>
                          </button>
                          {/* TopDesk ticket — one per conversation, not per message */}
                          {(() => {
                            const ts = conversationTicket.status;
                            const isDone = ts === 'done' || ts === 'attachment_failed';
                            const baseLabel = isDone
                              ? t('pagina:support-chat:topdesk:aangemaakt', 'TopDesk ticket created')
                              : ts === 'error'
                              ? t('pagina:support-chat:topdesk:fout', 'Failed to create ticket — click to try again')
                              : t('pagina:support-chat:topdesk:tooltip', 'Create a helpdesk ticket');
                            const label = isDone && conversationTicket.ticketNumber
                              ? `${baseLabel} (${conversationTicket.ticketNumber})`
                              : baseLabel;
                            return (
                              <button
                                onClick={() => (ts === 'idle' || ts === 'error') && enterTicketMode()}
                                disabled={ts === 'loading' || isDone || ticketMode}
                                className={`ferro-action-btn${isDone ? ' btn-done' : ts === 'error' ? ' btn-error' : ''}`}
                                title={label}
                                aria-label={label}
                              >
                                {ts === 'loading' ? (
                                  <svg className="w-[18px] h-[18px] animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                  </svg>
                                ) : isDone ? (
                                  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7a4 4 0 108 0 4 4 0 00-8 0M6 21v-2a4 4 0 014-4h2.5" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 19a2 2 0 00.914-3.782 2 2 0 00-2.414.483" />
                                    <circle cx="19" cy="22" r=".75" fill="currentColor" stroke="none" />
                                  </svg>
                                )}
                              </button>
                            );
                          })()}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ))}

              <div ref={messagesEndRef} />
            </div>{/* end max-w-3xl */}
            </div>

            {/* ── Bottom input bar ──────────────────────────────────────── */}
            <div className="ferro-input-area flex-shrink-0 px-6 py-4" style={{ borderTop: '1px solid rgba(201,214,241,0.6)', backgroundColor: 'rgba(236,240,249,0.8)' }}>
            <div className="max-w-4xl mx-auto w-full">
              {conversationClosedAt ? (
                /* ── Conversation closed: a TOPdesk ticket was created from it ── */
                <div className="flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-base text-emerald-800">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>
                      {t('pagina:support-chat:topdesk:gesloten', 'This conversation is closed — TOPdesk ticket {ticket} was created.')
                        .replace('{ticket}', conversationTicket.ticketNumber ?? '')}
                    </span>
                  </div>
                  <button onClick={startNewConversation}
                    className="flex-shrink-0 rounded-full bg-emerald-600 px-3 py-1 text-sm font-semibold text-white hover:bg-emerald-700 transition">
                    {tekst('pagina:support-chat:nieuwe-chat')}
                  </button>
                </div>
              ) : (
              <>
              {/* Ticket-mode banner */}
              {ticketMode && (
                <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-sky-200 bg-sky-50 px-4 py-2.5 text-base text-sky-800">
                  <span>
                    {t('pagina:support-chat:topdesk:invoer-uitleg', 'Describe what went wrong below — this will be used to create a TOPdesk helpdesk ticket, and the conversation will then be closed.')}
                  </span>
                  <button onClick={cancelTicketMode}
                    className="flex-shrink-0 rounded-full border border-sky-300 bg-white px-3 py-1 text-sm font-semibold text-sky-800 hover:bg-sky-100 transition">
                    {t('pagina:support-chat:annuleren', 'Cancel')}
                  </button>
                </div>
              )}

              {/* Context-size warnings */}
              {!ticketMode && currentConversationId && userMessageCount >= 10 ? (
                <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-800">
                  <div className="flex items-center gap-2">
                    <span>🚫</span>
                    <span>{tekst('pagina:support-chat:grens:bereikt')}</span>
                  </div>
                  <button onClick={startNewConversation}
                    className="flex-shrink-0 rounded-full bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700 transition">
                    {tekst('pagina:support-chat:nieuwe-chat')}
                  </button>
                </div>
              ) : !ticketMode && currentConversationId && userMessageCount === 9 ? (
                <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
                  <div className="flex items-center gap-2">
                    <span>⚠️</span>
                    <span><strong>{tekst('pagina:support-chat:grens:let-op')}:</strong> {tekst('pagina:support-chat:grens:laatste-vraag')}</span>
                  </div>
                  <button onClick={startNewConversation}
                    className="flex-shrink-0 rounded-full border border-amber-400 bg-white px-3 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100 transition">
                    {tekst('pagina:support-chat:nieuwe-chat')}
                  </button>
                </div>
              ) : null}

              {/* Attachment pills */}
              {attachments.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {attachments.map((att, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-1.5 bg-white rounded-lg pl-1 pr-1 py-1 shadow-sm"
                      style={{ border: '1px solid rgba(201,214,241,0.8)', maxWidth: '200px' }}
                    >
                      <img
                        src={att.dataUrl}
                        alt=""
                        className="w-7 h-7 object-cover rounded flex-shrink-0"
                      />
                      <span className="text-xs text-[#475569] truncate flex-1 min-w-0 max-w-[110px]">
                        {att.file.name}
                      </span>
                      <button
                        onClick={() => removeAttachment(i)}
                        className="flex-shrink-0 w-4 h-4 flex items-center justify-center rounded text-[#94a3b8] hover:text-[#dc2626] hover:bg-red-50 transition-colors text-sm leading-none"
                        title="Verwijder"
                        aria-label={`Verwijder ${att.file.name}`}
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Pill input */}
              <div className="flex items-center bg-white rounded-full px-4 py-1 gap-2 max-w-4xl mx-auto"
                style={{ boxShadow: '0px 4px 8px -2px rgba(23,23,23,0.06)', border: '1px solid rgba(201,214,241,0.6)' }}>

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleFileChange}
                />

                {/* Paperclip / attachment button */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading || ticketMode || (!!currentConversationId && userMessageCount >= 10)}
                  className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full transition-colors text-[#94a3b8] hover:text-[#005188] hover:bg-[#f1f5f9] disabled:opacity-40"
                  title="Afbeelding bijvoegen"
                  aria-label="Afbeelding bijvoegen"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                </button>

                <input
                  ref={inputRef}
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (ticketMode ? submitTicketComplaint() : sendMessage())}
                  onPaste={handlePaste}
                  placeholder={
                    ticketMode
                      ? t('pagina:support-chat:topdesk:invoer-placeholder', 'Describe what went wrong…')
                      : tekst('pagina:support-chat:invoer:placeholder', null, null, true)
                  }
                  disabled={isLoading || conversationTicket.status === 'loading' || (!ticketMode && !!currentConversationId && userMessageCount >= 10)}
                  className="flex-1 text-base bg-transparent outline-none py-3 text-[#475569] placeholder:text-[#94a3b8] disabled:opacity-50"
                />
                <button
                  onClick={ticketMode ? submitTicketComplaint : sendMessage}
                  disabled={
                    conversationTicket.status === 'loading' ||
                    (ticketMode
                      ? !inputMessage.trim()
                      : (isLoading || (!inputMessage.trim() && attachments.length === 0) || (!!currentConversationId && userMessageCount >= 10)))
                  }
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all disabled:opacity-40"
                  style={{ backgroundColor: '#23c1f4' }}
                >
                  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
                  </svg>
                </button>
              </div>
              </>
              )}
            </div>{/* end max-w-3xl */}
            </div>

          </div>{/* end main chat column */}
        </div>{/* end body */}
      </div>{/* end ferro-page */}
    </>
  );
}
